/** Motion System — shared tokens and variants for framer-motion.
 *
 * Centralises durations, easings, and reusable variants so every
 * animated surface in the app feels consistent. Keep these values
 * stable: tabs, cards, and lists all inherit from them.
 *
 * All consumers should import variants from here rather than inlining
 * magic numbers. If reduced-motion is enabled (handled globally via
 * <MotionConfig reducedMotion="user">), framer-motion automatically
 * collapses these transitions — no extra code is needed at the call
 * site.
 */
import type { Transition, Variants } from "framer-motion";

/** Motion durations in seconds (framer-motion expects seconds, not ms). */
export const durations = {
  /** 160ms — micro-interactions (hover, press). */
  fast: 0.16,
  /** 260ms — default page/section transitions. */
  base: 0.26,
  /** 420ms — emphasis transitions (rare, hero moments). */
  slow: 0.42,
} as const;

/** Custom cubic-bezier easings used across the app. */
export const easings = {
  /** Smooth ease-out — natural, no overshoot. Use for page transitions. */
  swift: [0.22, 1, 0.36, 1] as const,
  /** Slight overshoot — use sparingly for micro-impact (badge pops). */
  bounce: [0.34, 1.56, 0.64, 1] as const,
} as const;

/** Default transition applied to page-level movements. */
const pageTransitionSpec: Transition = {
  duration: durations.base,
  ease: easings.swift,
};

/** Page/tab transition — fade + gentle vertical slide. Enter from below,
 * exit upward so the eye naturally follows the change. */
export const pageTransition: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: pageTransitionSpec },
  exit: { opacity: 0, y: -8, transition: pageTransitionSpec },
};

/** Parent variant for stagger lists. Children inherit `fadeInUp` or
 * similar and fire sequentially. */
export const staggerContainer: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.02,
    },
  },
  exit: {},
};

/** Default child variant inside a stagger container. */
export const fadeInUp: Variants = {
  initial: { opacity: 0, y: 6 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: durations.base, ease: easings.swift },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: { duration: durations.fast, ease: easings.swift },
  },
};

/** Hover micro-interaction — subtle lift. Apply via whileHover on a
 * motion component. Scale is kept ≤ 1.01 to avoid blurring text. */
export const hoverLift = {
  whileHover: {
    y: -2,
    scale: 1.01,
    transition: { duration: durations.fast, ease: easings.swift },
  },
  whileTap: {
    scale: 0.995,
    transition: { duration: durations.fast, ease: easings.swift },
  },
} as const;
